/*
 * 开通弹窗自定义内容：存储入口 variant
 * 接入方：StorageEntranceCard（左下角 02 入口）+ VipEntranceBanner（顶部 01 入口）
 *
 * PRD 03 文案矩阵：
 * - 左下角入口：全 4 态都替换标题+副标题
 * - 顶部入口：仅「接近超限 / 已超限·未受限 / 已超限·已受限」替换
 *
 * 4 套标题×（非会员/会员）副标题映射，详见下方 RENDERER。
 */
import React from 'react'
import { registerPaymentContent } from '../components/VipPaymentModal/contentRegistry'
import { MEMBER_CONFIG } from '../privileges/constants'
import CONFIG from '../../constants/config'
import './storageEntranceVariant.scss'

export const STORAGE_ENTRANCE_VARIANT = 'storage-entrance'

export type StorageStage = 'normal' | 'near' | 'over_safe' | 'over_limited'

/** 进 Redux 的纯数据 · 不能塞 JSX */
export interface StorageEntranceData {
  /** 触发态 */
  stage: StorageStage
  /** 是否会员（含临期、过期；非会员=false） */
  isMember: boolean
  /** 已用照片数 */
  totalNum: number
  /** 当前等级照片上限 */
  limitPhotoNum: number
  /** near 态的剩余百分比（向上取整 1-10） */
  remainPercent?: number
  /** 会员等级名（基础会员/高级会员/超级会员）· isMember=true 时使用 */
  levelName?: string
}

/* 标题全黑（弹窗默认 color 即深色），副标题保留弹窗默认灰
 * tab 容量数字默认 muted、active tab 红色 · 由 storageEntranceVariant.scss 控制 */

/**
 * 各等级存储上限的单一来源是 `src/pages/privileges/constants.ts` 的 MEMBER_CONFIG，
 * 其中 VIP/VVIP/SVIP 的 features 里有 { label: '存储空间', value: 'X万张' }。
 * EVIP 当前不在 MEMBER_CONFIG（其权益是「不限量」），固定显示「更多 存储空间」。
 */
const getStorageValue = (vipKind: number): string => {
  const entry = MEMBER_CONFIG.find((m: any) => m.vipKind === vipKind)
  if (!entry) return ''
  const f = entry.features.find((x: any) => x.label === '存储空间')
  return f?.value ?? ''
}

const renderTabNote = (number: string, suffix: string) => (
  <>
    <span className="storage-tab-note__number">{number}</span>
    <span className="storage-tab-note__suffix">{suffix}</span>
  </>
)

/** 照片张数格式化 · ≥1 万显示「X 万」/ 否则千分位 */
const formatNum = (n: number): string => {
  if (n >= 10000) {
    const w = n / 10000
    return `${Number.isInteger(w) ? w : w.toFixed(1)} 万`
  }
  return n.toLocaleString('en-US')
}

registerPaymentContent(STORAGE_ENTRANCE_VARIANT, (d: StorageEntranceData) => {
  if (!d) return null

  // ===== 标题（统一全黑，跟随弹窗 header 默认色） =====
  let title: React.ReactNode
  switch (d.stage) {
    case 'normal':
      title = '开通会员，获得更多存储空间'
      break
    case 'near':
      title = `存储仅剩 ${d.remainPercent ?? 0}%，超限照片将无法查看`
      break
    case 'over_safe':
      title = '存储已满，超限照片将无法查看'
      break
    case 'over_limited':
      title = '存储已满，开通会员恢复查看'
      break
  }

  // ===== 副标题（按 isMember 分两套） =====
  let subtitle: React.ReactNode
  const totalText = formatNum(d.totalNum)
  // 会员配额短语 · limitPhotoNum 为 -1（后端「无限存储」哨兵值）时说「无限存储空间」，
  // 不显示「至多拥有 -1 张照片」
  const memberQuota =
    d.limitPhotoNum < 0
      ? '拥有无限存储空间'
      : `至多拥有 ${formatNum(d.limitPhotoNum)} 张照片`
  if (d.stage === 'normal') {
    subtitle = d.isMember ? (
      <>
        {d.levelName ?? '会员'}
        {memberQuota}，当前拥有 {totalText} 张
      </>
    ) : (
      <>当前拥有 {totalText} 张照片</>
    )
  } else {
    subtitle = d.isMember ? (
      <>
        {d.levelName ?? '会员'}
        {memberQuota}，升级会员获得更多存储空间
      </>
    ) : (
      <>当前拥有 {totalText} 张照片，开通会员获得更多存储空间</>
    )
  }

  // ===== 各 tab 下方小字（存储上限）· 4 个 tab 同步展示 =====
  // 容量数字从 MEMBER_CONFIG 读，等级扩容后这里自动同步
  const tabNotes = {
    basic: renderTabNote(getStorageValue(CONFIG.VIP_KIND.VIP), '照片'),
    advanced: renderTabNote(getStorageValue(CONFIG.VIP_KIND.VVIP), '照片'),
    super: renderTabNote(getStorageValue(CONFIG.VIP_KIND.SVIP), '照片'),
    enterprise: renderTabNote('更多', '存储空间')
  }

  return { title, subtitle, tabNotes }
})

/** 调用方组装 customContent 的便捷封装 · import 本函数 = 触发上面 registerPaymentContent */
export function storageEntranceCustomContent(data: StorageEntranceData) {
  return { variant: STORAGE_ENTRANCE_VARIANT, data }
}
